import { createSelector } from "@reduxjs/toolkit";
import sortBy from "lodash/sortBy";
import { selectCommon, selectSectionsReducer } from "../../util/selectors";
import { SECTION_PDP_TYPES } from "../../constants/sections";
import { RootState } from "../../redux/store";
import { Product } from "../../services/productService";

export const selectProductDetailReducer = (state: RootState) =>
  state.productDetailReducer;
export const selectProductDetails = createSelector(
  selectProductDetailReducer,
  (productReducer): Product => productReducer.productDetail || ({} as any)
);

export const selectPDPProductInfo = createSelector(
  selectProductDetails,
  ({ brand, title, id, captionImageURL }) => ({
    brand,
    title,
    productId: id,
    captionImageURL
  })
);

export const selectProductURL = createSelector(
  selectProductDetails,
  ({ url }) => url
);

export const selectProductTitleData = createSelector(
  selectProductDetails,
  ({ title }) => title
);

export const selectProductParents = createSelector(
  selectProductDetails,
  ({ parentCategory, category, subcategory }) => ({
    parentCategory,
    category,
    subcategory
  })
);

export const selectMapProductsUrl = createSelector(
  selectProductDetails,
  ({ parentCategory, category, subcategory }) => {
    return [parentCategory, category, subcategory].reduce(
      (urlsMap, item, index, originArray) => {
        const prevItem = originArray[index - 1];
        const nestedUrl = urlsMap.has(prevItem) ? urlsMap.get(prevItem) : "";
        urlsMap.set(item, `${nestedUrl}${item.key}/`);
        return urlsMap;
      },
      new Map()
    );
  }
);

export const selectIsProductMultisize = createSelector(
  selectProductDetails,
  ({ isMultisize }) => isMultisize
);

/**
 * size object shape example:
 * {
 * sizeType:{
 *  defaultValue: "US",
 *  key: "us",
 *  label:"US"
 * } or {},
 * variants:
 *  [{
 *   size: "5",sizeId: 180,
 *   sizeStock: 10,
 *   sortOrder: 50,
 *   upc: "4062061514854"
 *  }]
 * }
 */

export const selectProductSizes = createSelector(
  selectProductDetails,
  productDetails => productDetails.sizes || []
);

export const selectProductSizeTypes = createSelector(
  selectProductSizes,
  productSizes => productSizes.map(size => size.sizeType)
);

export const selectSortedProductSizeTypes = createSelector(
  selectProductSizes,
  productSizes =>
    sortBy(
      productSizes.map(size => size.sizeType),
      ["key"]
    )
);

export const selectPDPPriceInfo = createSelector(
  selectProductDetails,
  ({ price }) => ({
    currentPrice: price.current,
    oldPrice: price.old
  })
);

export const selectPDPBannerSections = createSelector(
  selectSectionsReducer,
  sectionReducer => {
    const {
      [SECTION_PDP_TYPES.PDP_TOP]: top,
      [SECTION_PDP_TYPES.PDP_SECTION_ONE]: one,
      [SECTION_PDP_TYPES.PDP_SECTION_TWO]: second
    } = sectionReducer.pdpSections;
    return [top, one, second];
  }
);

export const selectProductHighlights = createSelector(
  selectProductDetailReducer,
  productReducer => productReducer?.productDetail?.highlights
);

export const selectProductSizeChart = createSelector(
  selectProductDetailReducer,
  productDetailReducer => productDetailReducer.sizeChart
);

export const selectProductVeryFirstSize = createSelector(
  selectProductSizes,
  productSizes => productSizes[0]?.variants[0]
);

export const selectProductIsSingleSize = createSelector(
  selectProductVeryFirstSize,
  productFirstSize => productFirstSize?.sizeId === 0
);
